import type { ISODateTime, ResourceId, TaskId } from './branded.js';
import type { ConstraintType } from './task.js';

/** 所有 core 錯誤的錯誤碼（供 UI 做 i18n 對應）。 */
export type ProjectErrorCode =
  | 'CIRCULAR_DEPENDENCY'
  | 'CONSTRAINT_CONFLICT'
  | 'INVALID_CALENDAR'
  | 'CALENDAR_NOT_FOUND'
  | 'TASK_NOT_FOUND'
  | 'RESOURCE_NOT_FOUND'
  | 'INVALID_DEPENDENCY'
  | 'INVALID_HIERARCHY'
  | 'RESOURCE_OVERALLOCATED'
  | 'LEVELING_FAILED'
  | 'BASELINE_NOT_FOUND'
  | 'INVALID_ARGUMENT'
  | 'NOT_IMPLEMENTED';

/** core 所有錯誤的基底類別。 */
export abstract class ProjectError extends Error {
  abstract readonly code: ProjectErrorCode;

  protected constructor(message: string) {
    super(message);
    this.name = new.target.name;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** 相依關係形成環路。`cycle` 依序列出環上的任務，首尾為同一個 id。 */
export class CircularDependencyError extends ProjectError {
  override readonly code = 'CIRCULAR_DEPENDENCY' as const;
  readonly cycle: readonly TaskId[];

  constructor(cycle: readonly TaskId[]) {
    super(`Circular dependency detected: ${cycle.join(' -> ')}`);
    this.cycle = cycle;
  }
}

/** 限制條件與相依關係／日曆互相矛盾，無法產生合法排程。 */
export class ConstraintConflictError extends ProjectError {
  override readonly code = 'CONSTRAINT_CONFLICT' as const;
  readonly taskId: TaskId;
  readonly constraintType: ConstraintType;
  readonly constraintDate: ISODateTime | null;
  readonly conflictingTaskIds: readonly TaskId[];

  constructor(params: {
    taskId: TaskId;
    constraintType: ConstraintType;
    constraintDate: ISODateTime | null;
    conflictingTaskIds?: readonly TaskId[];
    detail?: string;
  }) {
    super(
      `Constraint ${params.constraintType} on task ${params.taskId} cannot be satisfied` +
        (params.detail ? `: ${params.detail}` : ''),
    );
    this.taskId = params.taskId;
    this.constraintType = params.constraintType;
    this.constraintDate = params.constraintDate;
    this.conflictingTaskIds = params.conflictingTaskIds ?? [];
  }
}

/** 日曆定義不合法（時段重疊、end <= start、繼承成環等）。 */
export class InvalidCalendarError extends ProjectError {
  override readonly code = 'INVALID_CALENDAR' as const;
  readonly calendarId: string;

  constructor(calendarId: string, detail: string) {
    super(`Invalid calendar ${calendarId}: ${detail}`);
    this.calendarId = calendarId;
  }
}

/** 參照到不存在的實體。 */
export class EntityNotFoundError extends ProjectError {
  override readonly code:
    | 'TASK_NOT_FOUND'
    | 'RESOURCE_NOT_FOUND'
    | 'CALENDAR_NOT_FOUND'
    | 'BASELINE_NOT_FOUND';
  readonly entityId: string;

  constructor(
    code:
      | 'TASK_NOT_FOUND'
      | 'RESOURCE_NOT_FOUND'
      | 'CALENDAR_NOT_FOUND'
      | 'BASELINE_NOT_FOUND',
    entityId: string,
  ) {
    super(`${code}: ${entityId}`);
    this.code = code;
    this.entityId = entityId;
  }
}

/** 相依關係本身不合法（自我相依、指向不存在的任務、跨層級違規等）。 */
export class InvalidDependencyError extends ProjectError {
  override readonly code = 'INVALID_DEPENDENCY' as const;
  readonly predecessorId: TaskId;
  readonly successorId: TaskId;

  constructor(predecessorId: TaskId, successorId: TaskId, detail: string) {
    super(`Invalid dependency ${predecessorId} -> ${successorId}: ${detail}`);
    this.predecessorId = predecessorId;
    this.successorId = successorId;
  }
}

/** 大綱結構不合法（parent 不存在、層級跳號、摘要任務成環等）。 */
export class InvalidHierarchyError extends ProjectError {
  override readonly code = 'INVALID_HIERARCHY' as const;
  readonly taskId: TaskId;

  constructor(taskId: TaskId, detail: string) {
    super(`Invalid task hierarchy at ${taskId}: ${detail}`);
    this.taskId = taskId;
  }
}

/** 資源撫平無法在給定選項下消除超額配置。 */
export class LevelingFailedError extends ProjectError {
  override readonly code = 'LEVELING_FAILED' as const;
  readonly resourceIds: readonly ResourceId[];

  constructor(resourceIds: readonly ResourceId[], detail: string) {
    super(`Resource leveling failed for [${resourceIds.join(', ')}]: ${detail}`);
    this.resourceIds = resourceIds;
  }
}

/** 引擎輸入參數不合法。 */
export class InvalidArgumentError extends ProjectError {
  override readonly code = 'INVALID_ARGUMENT' as const;

  constructor(message: string) {
    super(message);
  }
}

/** 尚未實作的契約佔位錯誤。 */
export class NotImplementedError extends ProjectError {
  override readonly code = 'NOT_IMPLEMENTED' as const;

  constructor(what: string) {
    super(`Not implemented: ${what}`);
  }
}

// ---------------------------------------------------------------------------
// 非致命的診斷訊息（引擎回傳而非拋出）
// ---------------------------------------------------------------------------

export type DiagnosticSeverity = 'Info' | 'Warning' | 'Error';

export interface Diagnostic {
  readonly severity: DiagnosticSeverity;
  readonly code: string;
  readonly message: string;
  readonly taskId?: TaskId;
  readonly resourceId?: ResourceId;
}
